import { Center, HStack, Switch } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { byBoolean, byDate, byNumber, byString, byValues } from "sort-es";

import CompactNumber from "@/common/components/CompactNumber";
import DataCell from "@/common/components/DataCell";
import { DataTable } from "@/common/components/DataTable/DataTable";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import TimestampCell from "@/common/components/TimestampCell";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSafeDataTableStateProps } from "@/common/hooks/useDataTableParamState";
import { useTableDisplayModeParam } from "@/common/hooks/useTableDisplayMode";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import {
  relativeTimeBucketLabel,
  relativeTimeBuckets,
} from "@/common/utils/datetime";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { getItemTree } from "@/common/utils/listItemTree";
import { FeatureNameCell } from "@/feature/components/FeatureNameCell";
import { TableDisplayModePicker } from "@/feature/components/TableDisplayModePicker";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { UserFlagsEmptyState } from "@/user/components/UserFlagsEmptyState";
import { UserFlagToggleDialog } from "@/user/components/UserFlagToggleDialog";
import { useUserData } from "@/user/data/useUserData";
import {
  defaultUserFlagsColumns,
  groupingUserFlagsColumns,
  UserFlagsColumn,
  useUserFlagsTableState,
} from "@/user/hooks/useUserTableStates";
import { FeatureName } from "@reflag/shared/featureAPI";
import { EntityFlagDTO, EntityFlagsResponse } from "@reflag/shared/flagAPI";
import { FeatureUrl } from "@reflag/shared/urls";
import {
  ColumnSorter,
  nullableSort,
  SortClauses,
} from "@reflag/shared/utils/sorting";

type UserFlagsTableProps = {
  data?: EntityFlagsResponse;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

type UserFlagTableRow = Omit<EntityFlagDTO, "id"> &
  Pick<
    FeatureName,
    | "icon"
    | "iconColor"
    | "source"
    | "isArchived"
    | "isPermanent"
    | "isStale"
    | "parentFeatureId"
  > & {
    id: string;
    featureId: string | null;
    flagId: string;
    subRows?: UserFlagTableRow[];
  };

export function UserFlagsTable({
  data,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: UserFlagsTableProps) {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: user } = useUserData(userId);
  const { data: featureNames = [], isLoading: isFeatureNamesLoading } =
    useFeatureNamesData();
  const [toggleFlag, setToggleFlag] = useState<Pick<
    EntityFlagDTO,
    "key" | "name" | "specificTargetValue"
  > | null>(null);

  const tableState = useUserFlagsTableState();
  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);
  const [displayMode] = useTableDisplayModeParam();

  const columnHelper = createSortableColumnHelper<
    UserFlagTableRow,
    UserFlagsColumn
  >();

  const featureByFlagKey = useMemo(
    () => new Map(featureNames.map((feature) => [feature.key, feature])),
    [featureNames],
  );

  const rows = useMemo(() => {
    const flags = data?.data ?? [];
    return flags.map((flag) => {
      const feature = featureByFlagKey.get(flag.key);
      return {
        ...flag,
        id: feature?.id ?? flag.id,
        featureId: feature?.id ?? null,
        flagId: flag.id,
        icon: feature?.icon ?? null,
        iconColor: feature?.iconColor ?? null,
        source: feature?.source ?? "event",
        isArchived: feature?.isArchived ?? false,
        isPermanent: feature?.isPermanent ?? false,
        isStale: feature?.isStale ?? false,
        parentFeatureId: feature?.parentFeatureId ?? null,
      } satisfies UserFlagTableRow;
    });
  }, [data?.data, featureByFlagKey]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: "Flag",
        cell: (cell) => {
          if (cell.row.original.featureId === null) {
            return (
              <DataCell fontSize="sm" type="string" value={cell.getValue()} />
            );
          }
          return <FeatureNameCell {...cell} />;
        },
      }),
      columnHelper.accessor("key", {
        header: "Key",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <DataCell
              fontFamily="code"
              fontSize="sm"
              type="string"
              value={value}
            />
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("value", {
        header: () => (
          <HStack>
            <span>Access</span>
          </HStack>
        ),
        cell: (cell) => {
          const { getValue, row } = cell;
          const isTargeted = getValue();

          if (row.getIsGrouped()) {
            return isTargeted ? "Enabled" : "Disabled";
          }

          const isSpecificallyTargeted =
            row.original.specificTargetValue === true;

          return (
            <HStack
              spacing={1}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Switch
                isChecked={isSpecificallyTargeted || isTargeted}
                isDisabled={(!isSpecificallyTargeted && isTargeted) || !user}
                size="sm"
                onChange={() => {
                  setToggleFlag({
                    key: row.original.key,
                    name: row.original.name,
                    specificTargetValue: row.original.specificTargetValue,
                  });
                }}
              />
              {!isSpecificallyTargeted && isTargeted && (
                <InfoIconTooltip text="This flag is implicitly enabled by the access rules. If one of the user's companies is targeted, it counts as implicitly targeted." />
              )}
            </HStack>
          );
        },
      }),
      columnHelper.accessor("firstExposureAt", {
        header: () => (
          <HStack>
            <span>First exposure</span>
            <InfoIconTooltip text="First time the user was exposed to this flag." />
          </HStack>
        ),
        cell: (cell) => {
          const value = cell.getValue();
          if (!value) return <NotAvailableCell />;
          return <TimestampCell color="dimmed" value={value} />;
        },
      }),
      columnHelper.accessor("lastExposureAt", {
        header: () => (
          <HStack>
            <span>Last exposure</span>
            <InfoIconTooltip text="Last time the user was exposed to this flag." />
          </HStack>
        ),
        cell: ({ row }) => {
          const { lastExposureAt } = row.original;
          if (!lastExposureAt) return <NotAvailableCell />;
          return <TimestampCell color="dimmed" value={lastExposureAt} />;
        },
      }),
      columnHelper.accessor("lastCheckAt", {
        header: () => (
          <HStack>
            <span>Last check</span>
            <InfoIconTooltip text="Last time the flag was checked for this user." />
          </HStack>
        ),
        cell: ({ row }) => {
          const { lastCheckAt } = row.original;
          if (!lastCheckAt) return <NotAvailableCell />;
          return <TimestampCell color="dimmed" value={lastCheckAt} />;
        },
      }),
      columnHelper.accessor("exposureCount", {
        header: "Exposure count",
        cell: ({ row }) => {
          const { exposureCount } = row.original;
          return exposureCount == null ? (
            <NotAvailableCell />
          ) : (
            <CompactNumber fontSize="sm" value={exposureCount} />
          );
        },
      }),
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (cell) => {
          const value = cell.getValue();
          if (cell.row.getIsGrouped()) {
            if (typeof value === "number") {
              return relativeTimeBucketLabel(value);
            }
            return relativeTimeBuckets(value);
          }
          return <TimestampCell value={value} />;
        },
        getGroupingValue: (row) => relativeTimeBuckets(row.createdAt, true),
      }),
      columnHelper.accessor("firstTrackAt", {
        header: "First track",
        cell: (cell) => {
          const value = cell.getValue();
          return value === null ? (
            <NotAvailableCell />
          ) : (
            <TimestampCell value={value} leftAlign />
          );
        },
      }),
      columnHelper.accessor("lastTrackAt", {
        header: "Last track",
        cell: (cell) => {
          const value = cell.getValue();
          return value === null ? (
            <NotAvailableCell />
          ) : (
            <TimestampCell value={value} leftAlign />
          );
        },
      }),
      columnHelper.accessor("trackCount", {
        header: "Track count",
        cell: ({ row }) => {
          const { trackCount } = row.original;
          return trackCount == null ? (
            <NotAvailableCell />
          ) : (
            <CompactNumber value={trackCount} />
          );
        },
      }),
    ];
  }, [columnHelper, user]);

  // Filter by search query
  const filteredFlags = useMemo(() => {
    const flags = rows;
    if (!tableState.searchQuery) return flags;
    const query = tableState.searchQuery.toLowerCase();
    return flags.filter(
      (f) =>
        f.id === tableState.searchQuery ||
        f.featureId === tableState.searchQuery ||
        f.flagId === tableState.searchQuery ||
        f.key.toLowerCase().includes(query) ||
        f.name.toLowerCase().includes(query),
    );
  }, [rows, tableState.searchQuery]);

  // DataTable doesn't sort these rows client-side in list/tree mode, so apply it here.
  const rowSortClauses = useMemo((): SortClauses<UserFlagTableRow> => {
    const [sort] = tableState.sorting;
    if (!sort) {
      return [];
    }

    return sortByUserFlagRowColumn(sort.id, sort.desc === true);
  }, [tableState.sorting]);

  const tableData = useMemo(() => {
    if (displayMode === "tree") {
      const tree = getItemTree(filteredFlags, "parentFeatureId");
      if (!rowSortClauses.length) {
        return tree;
      }
      return sortUserFlagTreeRows(tree, rowSortClauses);
    }

    if (!rowSortClauses.length) {
      return filteredFlags;
    }

    return [...filteredFlags].sort(byValues(rowSortClauses));
  }, [displayMode, filteredFlags, rowSortClauses]);

  if (isFeatureNamesLoading) {
    return (
      <Center flexGrow={1}>
        <DelayedSpinner />
      </Center>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={tableData}
        defaultColumns={defaultUserFlagsColumns}
        displayMode={displayMode}
        emptyState={
          <UserFlagsEmptyState
            isError={isError}
            isLoading={isLoading}
            onRetry={onRetry}
          />
        }
        groupingSorter={sortByUserFlagGroupColumn}
        isFetching={isFetching}
        meta={{ sortType: displayMode === "tree" ? "hierarchical" : "flat" }}
        searchPlaceholder="Search by name, key, or ID"
        tableId="user-flags"
        toolbarRightActions={() => (
          <TableDisplayModePicker<UserFlagsColumn>
            groupingColumn={tableState.groupingColumn}
            groupingColumns={groupingUserFlagsColumns}
            onGroupingColumnChange={tableState.onGroupingColumnChange}
          />
        )}
        variant="clickable"
        canCustomize
        canSearch
        canSort
        onRowClick={(row, e) => {
          if (row.original.featureId === null) {
            return;
          }
          const url = FeatureUrl(env, {
            id: row.original.featureId,
            name: row.original.name,
          });
          e.preventDefault();

          linkClickModifierHandler(url, () => {
            navigate(url);
          })(e);
        }}
        {...safeTableProps}
      />
      {toggleFlag && user && (
        <UserFlagToggleDialog
          flag={toggleFlag}
          isOpen={true}
          user={user}
          onClose={() => {
            setToggleFlag(null);
          }}
        />
      )}
    </>
  );
}

const sortByUserFlagGroupColumn: ColumnSorter<
  UserFlagTableRow,
  UserFlagsColumn
> = (sortBy, desc) => {
  switch (sortBy) {
    case "value":
      // Match CompanyFeatures grouping order: enabled/true group first.
      return [[(i) => i.value, byBoolean({ desc: !desc })]];
    case "createdAt":
      return [
        [(i) => relativeTimeBuckets(i.createdAt, true), byNumber({ desc })],
      ];
    default:
      return [];
  }
};

const sortByUserFlagRowColumn: ColumnSorter<
  UserFlagTableRow,
  UserFlagsColumn
> = (sortBy, desc) => {
  switch (sortBy) {
    case "name":
    case "key":
      return [[(i) => i[sortBy], byString({ lowercase: true, desc })]];
    case "value":
      return [[(i) => i.value, byBoolean({ desc: !desc })]];
    case "createdAt":
      return [[(i) => i.createdAt, byDate({ desc })]];
    case "firstExposureAt":
    case "lastExposureAt":
    case "lastCheckAt":
    case "firstTrackAt":
    case "lastTrackAt":
      return [
        [(i) => (i[sortBy] ? new Date(i[sortBy]) : null), nullableSort(desc)],
      ];
    case "exposureCount":
    case "trackCount":
      return [[(i) => i[sortBy], byNumber({ desc })]];
    default:
      return [];
  }
};

function sortUserFlagTreeRows(
  rows: UserFlagTableRow[],
  sortClauses: SortClauses<UserFlagTableRow>,
): UserFlagTableRow[] {
  return [...rows]
    .map((row) => ({
      ...row,
      subRows: row.subRows
        ? sortUserFlagTreeRows(row.subRows, sortClauses)
        : row.subRows,
    }))
    .sort(byValues(sortClauses));
}
